import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, ArrowRight, User, Tag, Share2 } from 'lucide-react';
import axios from 'axios';
import { trackPageView } from '../utils/analytics';
import SEO from './SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const res = await axios.get(`${API_URL}/api/blogs/${slug}`);
        setArticle(res.data);
        trackPageView(`/blog/${slug}`, res.data.title);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setNotFound(true);
        } else {
          console.error('Failed to fetch article:', err);
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchRecent = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/blogs`);
        const others = res.data.filter(a => a.slug !== slug).slice(0, 3);
        setRecentArticles(others);
      } catch (err) {
        console.error('Failed to fetch recent articles:', err);
      }
    };

    fetchArticle();
    fetchRecent();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>
          </div>
        </div>
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto animate-pulse space-y-6">
            <div className="h-6 bg-gray-200 rounded w-1/4" />
            <div className="h-10 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-96 bg-gray-200 rounded-2xl" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-testid="article-not-found">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            data-testid="back-to-blog-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${article.title} - Rumtek Finvest Blog`}
        description={article.excerpt}
        canonical={`/blog/${article.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": article.title,
          "description": article.excerpt,
          "author": { "@type": "Organization", "name": "Rumtek Finvest Pvt. Ltd." },
          "publisher": { "@type": "Organization", "name": "Rumtek Finvest Pvt. Ltd.", "logo": { "@type": "ImageObject", "url": "https://www.rumtek.co/rumtek-logo.jpg" } },
          "datePublished": article.date,
          "url": `https://www.rumtek.co/blog/${article.slug}`
        }}
      />
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Article Hero */}
      <article className="container mx-auto px-4 py-12" data-testid="blog-article-content">
        <div className="max-w-4xl mx-auto">
          {/* Category Badge */}
          <div className="mb-6">
            <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold" data-testid="article-category">
              {article.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-testid="article-title">
            {article.title}
          </h1>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8 pb-8 border-b">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span className="font-medium" data-testid="article-author">{article.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>
                {new Date(article.date).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{article.readTime}</span>
            </div>
            <button
              onClick={handleShare}
              className="ml-auto flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors"
              data-testid="share-article-btn"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </button>
          </div>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={article.image}
              alt={`${article.title} - Comprehensive guide on ${article.category} by SEBI registered financial advisor Rumtek Finvest India`}
              className="w-full h-96 object-cover"
              loading="eager"
            />
          </div>

          {/* Article Content */}
          <div 
            className="prose prose-lg max-w-none mb-12
              prose-headings:font-bold prose-headings:text-gray-900
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b-2 prose-h2:border-blue-200
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-blue-800
              prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-3 prose-h4:text-blue-700
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-6 prose-ul:space-y-2
              prose-ol:my-6 prose-ol:space-y-2
              prose-li:text-gray-700 prose-li:leading-relaxed
              prose-strong:text-gray-900 prose-strong:font-semibold
              prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: article.content }}
            data-testid="article-body"
          />

          {/* Tags */}
          <div className="mb-12 pb-12 border-b">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Topics:</h3>
            <div className="flex flex-wrap gap-3" data-testid="article-tags">
              {(article.tags || []).map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white text-center mb-12">
            <h3 className="text-2xl font-bold mb-3">Ready to Start Your Investment Journey?</h3>
            <p className="text-blue-100 mb-6">
              Get personalized advice from our experienced investment advisor India team. 
              Book a free consultation today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book-consultation"
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                data-testid="article-cta-consultation"
              >
                Book Free Consultation
              </Link>
              <Link
                to="/calculator"
                className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
                data-testid="article-cta-calculator"
              >
                Try Our Calculators
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {recentArticles.length > 0 && (
        <section className="bg-white py-16" data-testid="related-articles-section">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Read More Articles</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {recentArticles.map(relatedArticle => (
                  <Link
                    key={relatedArticle.id || relatedArticle.slug}
                    to={`/blog/${relatedArticle.slug}`}
                    className="group"
                    data-testid={`related-article-${relatedArticle.slug}`}
                  >
                    <article className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={relatedArticle.image}
                          alt={`${relatedArticle.title} - ${relatedArticle.category} advice by Rumtek Finvest financial advisors India`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-6">
                        <span className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
                          {relatedArticle.category}
                        </span>
                        <h3 className="text-lg font-bold text-gray-900 mt-3 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {relatedArticle.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {relatedArticle.excerpt}
                        </p>
                        <div className="flex items-center gap-2 text-blue-600 font-medium text-sm group-hover:gap-3 transition-all">
                          Read Article
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogArticle;
